import { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { CreditCard, AlertTriangle, Clock, Ban, RefreshCcw, Shield, FileText, DollarSign, Phone, Mail, Globe, CheckCircle2, XCircle } from 'lucide-react';

function PaymentPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "April 9, 2026";
  const companyName = "BlueCrate Group LLC";
  const companyAddress = "8 The Green STE B, Dover, DE 19901";
  const companyEmail = "billing@bluecrategroup.com";
  const companyPhone = "(555) 123-4567";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <CreditCard className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Payment Policy</h1>
            <p className="text-gray-600">Last Updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="prose prose-lg max-w-none">
              
              {/* Important Notice */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-red-900 mt-0 mb-2">IMPORTANT: READ BEFORE ORDERING</h3>
                    <p className="text-red-800 text-sm mb-0">
                      BY PLACING AN ORDER AND SUBMITTING PAYMENT, YOU AGREE TO ALL TERMS IN THIS PAYMENT POLICY. 
                      THIS INCLUDES OUR NON-REFUNDABLE DEPOSIT POLICY, LATE PAYMENT FEES, AND CHARGEBACK TERMS. 
                      PLEASE REVIEW CAREFULLY BEFORE PROCEEDING WITH ANY PURCHASE.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-10">
                <h3 className="font-bold text-blue-900 mt-0 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Payment Terms at a Glance
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white rounded-xl p-4">
                    <p className="font-bold text-gray-900 mb-1">Deposit Required</p>
                    <p className="text-2xl font-bold text-primary">30%</p>
                    <p className="text-gray-600">of total order value</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="font-bold text-gray-900 mb-1">Balance Due</p>
                    <p className="text-2xl font-bold text-primary">70%</p>
                    <p className="text-gray-600">upon production completion</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="font-bold text-gray-900 mb-1">Late Payment Fee</p>
                    <p className="text-2xl font-bold text-amber-600">3%</p>
                    <p className="text-gray-600">per month on overdue balances</p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="font-bold text-gray-900 mb-1">Card Service Fee</p>
                    <p className="text-2xl font-bold text-gray-700">3%</p>
                    <p className="text-gray-600">waived for wire/check payments</p>
                  </div>
                </div>
              </div>

              {/* Section 1 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Payment Structure
                </h2>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">1.1 Deposit Requirement</h3>
                <p>
                  All orders require a minimum <strong>30% deposit</strong> to secure your order and reserve container 
                  space. The deposit is calculated based on the total product cost before taxes and fees.
                </p>
                <div className="bg-gray-50 rounded-xl p-4 my-4">
                  <p className="font-semibold text-gray-900 mb-2">Example Calculation:</p>
                  <ul className="text-sm space-y-1">
                    <li>Total Product Cost: $10,000</li>
                    <li>30% Deposit: $3,000</li>
                    <li>+ Applicable Sales Tax on Deposit</li>
                    <li>+ 3% Service Fee (card payments only)</li>
                    <li>= Amount Due at Checkout</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">1.2 Balance Payment</h3>
                <p>
                  The remaining <strong>70% balance is due upon production completion</strong>. You will receive an 
                  email notification when your order is ready, and payment must be made before shipping can be arranged.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">Balance Payment Timeline</p>
                      <p className="text-amber-800 text-sm">
                        Balance payment is due immediately upon production completion notification. Orders with 
                        unpaid balances will not be shipped and will incur late fees.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">1.3 Full Payment Option</h3>
                <p>
                  You may choose to pay <strong>100% of the order value upfront</strong> at checkout. This does not 
                  accelerate production timelines but eliminates the need for a second payment transaction.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Accepted Payment Methods
                </h2>

                <div className="space-y-4">
                  {/* Credit/Debit Card */}
                  <div className="border-2 border-gray-200 rounded-xl p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <CreditCard className="w-6 h-6 text-primary mt-1" />
                        <div>
                          <h4 className="font-bold text-lg">Credit/Debit Card</h4>
                          <p className="text-gray-600 text-sm">Visa, Mastercard, American Express, Discover</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full">3% Fee</span>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>Instant processing</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>3D Secure verification for fraud protection</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>PCI-DSS compliant processing</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span>3% service fee applies</span>
                      </li>
                    </ul>
                  </div>

                  {/* Wire Transfer */}
                  <div className="border-2 border-green-200 bg-green-50/50 rounded-xl p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <DollarSign className="w-6 h-6 text-green-600 mt-1" />
                        <div>
                          <h4 className="font-bold text-lg">Wire Transfer (ACH)</h4>
                          <p className="text-gray-600 text-sm">Direct bank-to-bank transfer</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">No Fee</span>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>No service fee - save 3%</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>Preferred for large orders</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span>72-hour hold while awaiting payment confirmation</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span>2-3 business days for funds to clear</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">
                        Bank details will be emailed after order placement. Include your order number in the wire memo.
                      </p>
                    </div>
                  </div>

                  {/* Check */}
                  <div className="border-2 border-green-200 bg-green-50/50 rounded-xl p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <FileText className="w-6 h-6 text-green-600 mt-1" />
                        <div>
                          <h4 className="font-bold text-lg">Cashier's Check / Official Bank Check</h4>
                          <p className="text-gray-600 text-sm">Mailed payment</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">No Fee</span>
                    </div>
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800">
                          <strong>CASHIER'S/OFFICIAL BANK CHECKS ONLY.</strong> Personal checks are NOT accepted and will be returned.
                        </p>
                      </div>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>No service fee - save 3%</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span>72-hour hold while awaiting check receipt</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Mail checks to:</p>
                      <p className="text-sm text-gray-700">
                        {companyName}<br />
                        {companyAddress}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        Include your order number on the check memo line.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  Taxes and Fees
                </h2>

                <h3 className="text-xl font-semibold mt-6 mb-3">3.1 Sales Tax</h3>
                <p>
                  Sales tax is calculated based on the delivery address and applicable state/local tax rates. Tax is 
                  applied to both the deposit payment and the balance payment.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">3.2 Tax Exempt Purchases</h3>
                <p>
                  If you qualify for tax exemption (resellers, contractors with valid exemption certificates, etc.), 
                  you must provide valid tax exemption documentation within <strong>72 hours</strong> of placing your 
                  order. Accepted documents include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>State-issued resale certificate</li>
                  <li>Tax exemption certificate</li>
                  <li>Contractor's license with tax-exempt status</li>
                </ul>
                <p className="mt-4">
                  Orders placed as tax-exempt without valid documentation within the required timeframe will be 
                  charged applicable sales tax.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">3.3 Service Fee</h3>
                <p>
                  A <strong>3% service fee</strong> is applied to all credit/debit card transactions to cover payment 
                  processing costs. This fee is waived for wire transfer and check payments.
                </p>
              </section>

              {/* Section 4 - Critical */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <span className="text-red-700">Late Payment Policy</span>
                </h2>

                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-red-900 mt-0 mb-4">LATE PAYMENT CONSEQUENCES</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="w-5 h-5 text-red-600" />
                        <h4 className="font-bold text-red-900">3% Monthly Late Fee</h4>
                      </div>
                      <p className="text-red-800 text-sm">
                        A late fee of <strong>3% per month</strong> (36% APR) will be applied to any balance not paid 
                        by the due date. Late fees compound monthly and are added to the outstanding balance.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Ban className="w-5 h-5 text-red-600" />
                        <h4 className="font-bold text-red-900">Shipment Hold</h4>
                      </div>
                      <p className="text-red-800 text-sm">
                        Orders with unpaid balances will <strong>NOT be shipped</strong>. Your products will be held 
                        in our warehouse until full payment (including any late fees) is received.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-red-600" />
                        <h4 className="font-bold text-red-900">Storage Fees</h4>
                      </div>
                      <p className="text-red-800 text-sm">
                        After <strong>30 days</strong> of non-payment past the due date, additional storage fees of 
                        <strong> $0.50 per square foot per month</strong> will be assessed.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <div className="flex items-center gap-3 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h4 className="font-bold text-red-900">Forfeiture and Collections</h4>
                      </div>
                      <p className="text-red-800 text-sm">
                        After <strong>90 days</strong> of non-payment, your order may be forfeited, your deposit will 
                        NOT be refunded, and the remaining balance (plus fees) may be sent to collections. 
                        Collection actions may be reported to credit bureaus.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 5 - Refunds */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                  Cancellations and Refunds
                </h2>

                <div className="space-y-4">
                  {/* Before Production */}
                  <div className="border-2 border-green-200 rounded-xl overflow-hidden">
                    <div className="bg-green-100 px-5 py-3">
                      <h4 className="font-bold text-green-900 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        Before Production Begins
                      </h4>
                    </div>
                    <div className="p-5">
                      <p className="mb-3">
                        If you cancel <strong>before your container reaches 80% capacity</strong> (production trigger):
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                          <span>Refund of deposit <strong>minus 10% processing fee</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                          <span>Service fees (3% card processing) are non-refundable</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                          <span>Refunds processed within 5-10 business days</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* After Production Starts */}
                  <div className="border-2 border-amber-200 rounded-xl overflow-hidden">
                    <div className="bg-amber-100 px-5 py-3">
                      <h4 className="font-bold text-amber-900 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        After Production Begins
                      </h4>
                    </div>
                    <div className="p-5">
                      <p className="mb-3">
                        Once your container reaches 80% capacity and a Purchase Order is sent to the factory:
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                          <span><strong>Deposit is NON-REFUNDABLE and forfeited</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                          <span>You remain obligated to pay the 70% balance upon completion</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                          <span>Cancellation does not release you from payment obligation</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* After Production Complete */}
                  <div className="border-2 border-red-200 rounded-xl overflow-hidden">
                    <div className="bg-red-100 px-5 py-3">
                      <h4 className="font-bold text-red-900 flex items-center gap-2">
                        <XCircle className="w-5 h-5" />
                        After Production Complete
                      </h4>
                    </div>
                    <div className="p-5">
                      <p className="mb-3">
                        Once production is complete and your order is ready for shipment:
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span><strong>NO CANCELLATION PERMITTED</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>Full payment (100%) is required</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>Refusal to pay will result in collection action</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-5 mt-6">
                  <h4 className="font-bold mb-3">How to Request Cancellation</h4>
                  <p className="text-sm text-gray-700">
                    To request a cancellation, email {companyEmail} with your order number and reason for cancellation. 
                    Cancellation requests are processed within 2 business days. You will receive confirmation of your 
                    cancellation status and any applicable refund amount.
                  </p>
                </div>
              </section>

              {/* Section 6 - Chargebacks */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                  <span className="text-red-700">Chargebacks and Disputes</span>
                </h2>

                <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-red-900 mt-0 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6" />
                    CHARGEBACK POLICY
                  </h3>
                  
                  <div className="space-y-4 text-red-800">
                    <p>
                      <strong>WE ACTIVELY DISPUTE ALL ILLEGITIMATE CHARGEBACKS.</strong> By placing an order, you agree that:
                    </p>
                    
                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <h4 className="font-bold text-red-900 mb-2">Contact Us First</h4>
                      <p className="text-sm">
                        Before initiating any payment dispute with your bank or credit card company, you MUST contact 
                        us at {companyEmail} to attempt resolution. We are committed to resolving issues fairly and 
                        promptly. Disputes raised directly with us can typically be resolved within 48-72 hours.
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <h4 className="font-bold text-red-900 mb-2">Chargeback Consequences</h4>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>All chargebacks for valid orders will be disputed with full documentation</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>A <strong>$50 administrative fee</strong> will be charged for any chargeback that we successfully dispute</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>Your account will be permanently suspended</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-2"></span>
                          <span>Outstanding balances will be sent to collections</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-red-200">
                      <h4 className="font-bold text-red-900 mb-2">Fraud Warning</h4>
                      <p className="text-sm">
                        <strong>FILING A FRAUDULENT CHARGEBACK IS ILLEGAL.</strong> We maintain comprehensive records 
                        of all transactions including IP addresses, device information, email verifications, and 
                        delivery confirmations. Fraudulent chargebacks will be reported to law enforcement and 
                        prosecuted to the fullest extent of the law.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mt-6">
                  <h4 className="font-bold text-blue-900 mb-2">Legitimate Dispute Resolution</h4>
                  <p className="text-sm text-blue-800">
                    If you believe there has been an error with your charge, please contact us immediately. Valid 
                    reasons for disputes include unauthorized transactions, duplicate charges, or charges for 
                    undelivered/defective goods (with proper documentation). We will investigate all claims promptly 
                    and issue refunds where appropriate.
                  </p>
                </div>
              </section>

              {/* Section 7 - Order Modifications */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
                  Order Modifications
                </h2>

                <h3 className="text-xl font-semibold mt-6 mb-3">7.1 Before Production</h3>
                <p>
                  Order modifications (quantity changes, product changes) may be requested before production begins. 
                  Changes are subject to availability and may affect pricing.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Quantity increases: Additional deposit required for the difference</li>
                  <li>Quantity decreases: Limited to 10% reduction; partial refund minus processing fees</li>
                  <li>Product changes: Subject to availability and price adjustment</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3">7.2 After Production Begins</h3>
                <p>
                  Once production begins (container reaches 80% and PO is sent to factory), <strong>NO modifications 
                  are permitted</strong>. You are committed to your original order quantity and specifications.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">7.3 Address Changes</h3>
                <p>
                  Shipping address changes must be requested at least 7 days before estimated shipment. Changes 
                  requested after this window may incur re-routing fees.
                </p>
              </section>

              {/* Section 8 - Fraud Prevention */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">8</span>
                  Fraud Prevention and Verification
                </h2>

                <p>
                  To protect our business and legitimate customers, we employ various fraud prevention measures:
                </p>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <h4 className="font-bold">Email Verification</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      All orders require email verification via 6-digit code before payment.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <h4 className="font-bold">3D Secure</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      Card payments are protected by 3D Secure bank verification.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <h4 className="font-bold">Manual Review</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      Large orders may require additional verification and manual approval.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <h4 className="font-bold">Address Verification</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      Billing address must match the card on file (AVS check).
                    </p>
                  </div>
                </div>

                <p className="mt-4">
                  We reserve the right to cancel any order that fails verification or appears fraudulent, without 
                  prior notice. Suspected fraud will be reported to appropriate authorities.
                </p>
              </section>

              {/* Section 9 - Currency */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">9</span>
                  Currency and International Orders
                </h2>

                <p>
                  All prices are quoted and charged in <strong>United States Dollars (USD)</strong>. International 
                  customers are responsible for any currency conversion fees charged by their banks or payment providers.
                </p>
                <p className="mt-4">
                  We currently only ship within the continental United States. International shipping inquiries 
                  may be directed to {companyEmail}.
                </p>
              </section>

              {/* Section 10 - Contact */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">10</span>
                  Billing Questions and Support
                </h2>

                <p>
                  For questions about payments, billing, refunds, or any financial matters:
                </p>
                
                <div className="bg-gray-50 rounded-xl p-6 mt-4">
                  <p className="font-bold text-lg mb-4">{companyName} - Billing Department</p>
                  <div className="space-y-2 text-gray-700">
                    <p className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary" />
                      <span>Email: {companyEmail}</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary" />
                      <span>Phone: {companyPhone}</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-primary" />
                      <span>Address: {companyAddress}</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <span>Hours: Monday - Friday, 9:00 AM - 5:00 PM EST</span>
                    </p>
                  </div>
                </div>
              </section>

              {/* Final Acknowledgment */}
              <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 mt-10">
                <h3 className="font-bold text-gray-900 mb-4">ACKNOWLEDGMENT AND AGREEMENT</h3>
                <p className="text-gray-800 mb-4">
                  By placing an order with {companyName}, you acknowledge that you have read, understood, and agree 
                  to be bound by this Payment Policy. This includes but is not limited to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>The 30% deposit requirement and non-refundable deposit terms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>The 70% balance due upon production completion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>The 3% monthly late fee on overdue balances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>The cancellation and refund policy at each production stage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>The chargeback policy and dispute resolution procedures</span>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPolicy;
